import { html, LitElement, TemplateResult } from "lit";
import {
  customElement,
  property,
  query,
  queryAssignedNodes,
} from "lit/decorators.js";
import { ClassInfo, classMap } from "lit/directives/class-map.js";

import { CARD_EVENT_CLICK, CardPaddingSize } from "./card.constants";
import { style } from "./assets/card.generated-style";

/**
 * Cards contain content and actions about a single item.
 *
 * @CustomElement slh-card
 *
 * @cssprop --slh-default-font-family - Default card font family.
 * @cssprop --slh-color-neutral-shade-00 - Card background color.
 * @cssprop --slh-color-neutral-shade-60 - Content text color.
 * @cssprop --slh-color-neutral - Card title text color.
 * @cssprop --slh-base-xxs-line-height - Content text line height.
 * @cssprop --slh-base-bold-font-weight - Bold text.
 * @cssprop --slh-base-semi-bold-font-weight - Semi bold text.
 * @cssprop --slh-base-m-font-size - Card title font size.
 * @cssprop --slh-base-xxs-font-size - Card content font size.
 * @cssprop --slh-color-neutral-shade-40 - Disabled card title text color.
 * @cssprop --slh-color-outline - Focused element on tab border color.
 *
 * ** slots
 * @slot state - The top right element over the image.
 * @slot image - The element that holds the image.
 * @slot content-header - The element below the image.
 * @slot title - The element for larger text.
 * @slot content - The element for lighter and smaller text.
 * @slot actions - slh-button holder.
 */
@customElement("slh-card")
export default class SlhCard extends LitElement {
  @queryAssignedNodes({ slot: "image" })
  private imageSlotNodes: Array<HTMLElement> | undefined;

  @queryAssignedNodes({ slot: "content-header" })
  private contentHeaderSlotNodes: Array<HTMLElement> | undefined;

  @queryAssignedNodes({ slot: "title" })
  private titleSlotNodes: Array<HTMLElement> | undefined;

  @queryAssignedNodes({ slot: "content" })
  private contentSlotNodes: Array<HTMLElement> | undefined;

  @queryAssignedNodes({ slot: "actions" })
  private actionsSlotNodes: Array<HTMLElement> | undefined;

  @query(".image-container", false)
  private imageContainer!: HTMLElement;

  @query(".content-container", false)
  private contentContainer!: HTMLElement;

  @query("slot[name=title]", false)
  private titleSlot: HTMLElement | undefined;

  @query("slot[name=content]", false)
  private contentSlot: HTMLElement | undefined;

  @query("slot[name=actions]", false)
  private actionsSlot: HTMLElement | undefined;

  static styles = style;

  /**
   * Show two columns of card content in mobile view
   */
  @property({
    type: Boolean,
    attribute: "two-column",
    reflect: true,
  })
  twoColumn: boolean = false;

  /**
   * Sets the padding size for card content container. Options: "sm" (default), "md".
   */
  @property({
    type: CardPaddingSize,
    attribute: true,
    reflect: true,
  })
  padding: CardPaddingSize = CardPaddingSize.SM;

  /**
   * When set, card image and title are rendered in disabled-alike state (greyed out).
   */
  @property({
    type: Boolean,
    attribute: true,
    reflect: true,
  })
  disabled: boolean = false;

  /**
   * Elevated 1 style
   */
  @property({
    type: Boolean,
    attribute: true,
  })
  elevated1: boolean = false;

  connectedCallback(): void {
    super.connectedCallback();
  }

  /**
   * @fires "click-card"
   */
  private clickEvent() {
    const customEvent = new CustomEvent(CARD_EVENT_CLICK, {
      detail: {
        data: this.dataset,
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(customEvent);
  }

  private addContentContainerClass() {
    if (!this.hasCardContent()) {
      this.contentContainer.classList.remove("padding-sm", "padding-md");
    } else if (this.padding === CardPaddingSize.SM) {
      this.contentContainer.classList.add("padding-sm");
    } else if (this.padding === CardPaddingSize.MD) {
      this.contentContainer.classList.add("padding-md");
    }
  }

  private hasCardContent() {
    return (
      this.contentHeaderSlotNodes?.length !== 0 ||
      this.titleSlotNodes?.length !== 0 ||
      this.contentSlotNodes?.length !== 0 ||
      this.actionsSlotNodes?.length !== 0
    );
  }

  private addImageContainerClass() {
    if (this.hasCardContent()) {
      this.imageContainer.classList.add("image-container-layout");
      if (this.padding === CardPaddingSize.MD) {
        this.imageContainer.classList.add("padding-md");
      } else {
        this.imageContainer.classList.remove("padding-md");
      }
    } else {
      this.imageContainer.classList.remove(
        "image-container-layout",
        "padding-sm",
        "padding-md"
      );
    }
  }

  protected firstUpdated() {
    this.addContentContainerClass();
    this.emptyImageContainerClass();
    this.addTitleClass();
    this.addContentClass();
    this.addActionsClass();
  }

  private addTitleClass() {
    if (this.titleSlotNodes?.length === 0) {
      this.titleSlot?.classList.add("hidden");
    } else {
      this.titleSlot?.classList.remove("hidden");
    }

    if (this.contentHeaderSlotNodes?.length === 0 && this.titleSlot) {
      this.titleSlot.classList.remove("pt-l");
    } else if (this.titleSlot) {
      this.titleSlot.classList.add("pt-l");
    }
  }

  private addContentClass() {
    if (this.contentSlotNodes?.length === 0) {
      this.contentSlot?.classList.add("hidden");
    } else {
      this.contentSlot?.classList.remove("hidden");
    }

    if (
      this.contentHeaderSlotNodes?.length === 0 &&
      this.titleSlotNodes?.length === 0 &&
      this.contentSlot
    ) {
      this.contentSlot.classList.remove("pt-s");
    } else if (this.contentSlot) {
      this.contentSlot.classList.add("pt-s");
    }
  }

  private addActionsClass() {
    if (this.actionsSlotNodes?.length === 0) {
      this.actionsSlot?.classList.add("hidden");
    } else {
      this.actionsSlot?.classList.remove("hidden");
    }

    if (
      this.contentHeaderSlotNodes?.length === 0 &&
      this.titleSlotNodes?.length === 0 &&
      this.contentSlotNodes?.length === 0 &&
      this.actionsSlot
    ) {
      this.actionsSlot.classList.remove("pt-xl");
    } else if (this.actionsSlot) {
      this.actionsSlot.classList.add("pt-xl");
    }
  }

  private emptyImageContainerClass() {
    if (this.imageSlotNodes?.length) {
      this.imageContainer.classList.remove("empty-image-container");
    } else {
      this.imageContainer.classList.add("empty-image-container");
    }
  }

  protected onStateSlotChange(): void {
    this.addImageContainerClass();
  }

  protected onImageSlotChange(): void {
    this.emptyImageContainerClass();
  }

  protected onContentHeaderSlotChange(): void {
    this.addContentContainerClass();
  }

  protected onTitleSlotChange(): void {
    this.addContentContainerClass();
    this.addTitleClass();
  }

  protected onContentSlotChange(): void {
    this.addContentContainerClass();
    this.addContentClass();
  }

  protected onActionsSlotChange(): void {
    this.addContentContainerClass();
    this.addActionsClass();
  }

  /**
   * @returns {ClassInfo}
   */
  renderClasses(): ClassInfo {
    return {
      "two-column": this.twoColumn,
      elevated1: this.elevated1,
      disabled: this.disabled,
    };
  }

  /**
   * @returns {ClassInfo}
   */
  renderContentClasses(): ClassInfo {
    return {
      "padding-sm": this.padding === CardPaddingSize.SM,
      "padding-md": this.padding === CardPaddingSize.MD,
    };
  }

  /**
   * @returns {string} render html template
   */
  render(): TemplateResult {
    return html`
      <div
        class="card-container ${classMap(this.renderClasses())}"
        @click="${this.clickEvent}"
        tabindex="0"
      >
        <div class="image-container">
          <slot name="state" @slotchange="${this.onStateSlotChange}"></slot>
          <slot name="image" @slotchange="${this.onImageSlotChange}"></slot>
        </div>
        <div class="content-container ${classMap(this.renderContentClasses())}">
          <div class="content-holder">
            <slot
              name="content-header"
              @slotchange="${this.onContentHeaderSlotChange}"
            ></slot>
            <slot name="title" @slotchange="${this.onTitleSlotChange}"></slot>
            <slot
              name="content"
              @slotchange="${this.onContentSlotChange}"
            ></slot>
          </div>
          <slot name="actions" @slotchange="${this.onActionsSlotChange}"></slot>
        </div>
      </div>
    `;
  }
}
